/**
 * Statistics model for tracking user achievements and progress
 */
class Statistics {
    constructor() {
        this.totalContributions = 0;
        this.languagesContributed = new Set();
        this.placeTypesContributed = new Set();
        this.regionsContributed = new Set();
        this.contributionsByLanguage = new Map();
        this.contributionsByPlaceType = new Map();
        this.contributionsByDate = new Map();
        this.streakDays = 0;
        this.longestStreak = 0;
        this.lastContributionDate = null;
        this.joinDate = Date.now();
        this.qualityScore = 0;
        this.badgesEarned = [];
        this.seenPlaces = 0;
        this.wikidataUploads = 0;
        this.validationContributions = 0;
    }

    /**
     * Add a new contribution to statistics
     */
    addContribution(contribution) {
        this.totalContributions++;
        
        // Track language statistics
        const languageCode = contribution.language?.ietfLanguageTag || 
                           contribution.language?.customLanguageCode || 'unknown';
        this.languagesContributed.add(languageCode);
        
        const langCount = this.contributionsByLanguage.get(languageCode) || 0;
        this.contributionsByLanguage.set(languageCode, langCount + 1);

        // Track place type statistics
        const placeType = contribution.place?.matchedType || 'unknown';
        this.placeTypesContributed.add(placeType);
        
        const typeCount = this.contributionsByPlaceType.get(placeType) || 0;
        this.contributionsByPlaceType.set(placeType, typeCount + 1);

        // Track daily contributions and streaks
        const today = new Date().toDateString();
        const dailyCount = this.contributionsByDate.get(today) || 0;
        this.contributionsByDate.set(today, dailyCount + 1);
        
        this.updateStreak(today);
        this.lastContributionDate = Date.now();

        // Track regions (simplified - using lat/lon ranges)
        if (contribution.place?.lat && contribution.place?.lon) {
            const region = this.getRegionKey(contribution.place.lat, contribution.place.lon);
            this.regionsContributed.add(region);
        }
    }

    /**
     * Add a seen place (marked without label)
     */
    addSeenPlace() {
        this.seenPlaces++;
    }

    /**
     * Add a Wikidata upload
     */
    addWikidataUpload() {
        this.wikidataUploads++;
    }

    /**
     * Add validation contribution
     */
    addValidation() {
        this.validationContributions++;
    }

    /**
     * Update contribution streak
     */
    updateStreak(today) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toDateString();

        if (this.contributionsByDate.has(yesterdayStr) || this.streakDays === 0) {
            this.streakDays++;
        } else {
            this.streakDays = 1; // Reset streak
        }

        this.longestStreak = Math.max(this.longestStreak, this.streakDays);
    }

    /**
     * Get region key for geographic grouping
     */
    getRegionKey(lat, lon) {
        // Simple region grouping by 2-degree squares
        const latKey = Math.floor(lat / 2) * 2;
        const lonKey = Math.floor(lon / 2) * 2;
        return `${latKey},${lonKey}`;
    }

    /**
     * Calculate quality score based on various factors
     */
    calculateQualityScore() {
        let score = 0;
        
        // Base score from contributions
        score += this.totalContributions;
        
        // Bonus for language diversity
        score += this.languagesContributed.size * 5;
        
        // Bonus for place type diversity
        score += this.placeTypesContributed.size * 3;
        
        // Bonus for geographic diversity
        score += this.regionsContributed.size * 2;
        
        // Bonus for streak
        score += this.longestStreak * 2;
        
        // Bonus for validation contributions
        score += this.validationContributions * 3;
        
        this.qualityScore = score;
        return score;
    }

    /**
     * Get contribution statistics by language
     */
    getLanguageStats() {
        return Array.from(this.contributionsByLanguage.entries())
            .map(([language, count]) => ({ language, count }))
            .sort((a, b) => b.count - a.count);
    }

    /**
     * Get contribution statistics by place type
     */
    getPlaceTypeStats() {
        return Array.from(this.contributionsByPlaceType.entries())
            .map(([type, count]) => ({ type, count }))
            .sort((a, b) => b.count - a.count);
    }

    /**
     * Get recent contribution activity
     */
    getRecentActivity(days = 30) {
        const activity = [];
        const today = new Date();
        
        for (let i = 0; i < days; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            const dateStr = date.toDateString();
            
            activity.push({
                date: dateStr,
                count: this.contributionsByDate.get(dateStr) || 0
            });
        }
        
        return activity.reverse();
    }

    /**
     * Get achievement progress for specific milestones
     */
    getAchievementProgress() {
        return {
            contributions: {
                current: this.totalContributions,
                milestones: [1, 10, 50, 100, 500]
            },
            languages: {
                current: this.languagesContributed.size,
                milestones: [1, 3, 5, 10]
            },
            streak: {
                current: this.streakDays,
                longest: this.longestStreak,
                milestones: [7, 30, 100]
            },
            quality: {
                current: this.calculateQualityScore(),
                milestones: [50, 200, 500, 1000]
            }
        };
    }

    /**
     * Export statistics for persistence
     */
    toJSON() {
        return {
            totalContributions: this.totalContributions,
            languagesContributed: Array.from(this.languagesContributed),
            placeTypesContributed: Array.from(this.placeTypesContributed),
            regionsContributed: Array.from(this.regionsContributed),
            contributionsByLanguage: Array.from(this.contributionsByLanguage.entries()),
            contributionsByPlaceType: Array.from(this.contributionsByPlaceType.entries()),
            contributionsByDate: Array.from(this.contributionsByDate.entries()),
            streakDays: this.streakDays,
            longestStreak: this.longestStreak,
            lastContributionDate: this.lastContributionDate,
            joinDate: this.joinDate,
            qualityScore: this.qualityScore,
            badgesEarned: this.badgesEarned,
            seenPlaces: this.seenPlaces,
            wikidataUploads: this.wikidataUploads,
            validationContributions: this.validationContributions
        };
    }

    /**
     * Import statistics from saved data
     */
    static fromJSON(data) {
        const stats = new Statistics();
        
        if (data) {
            stats.totalContributions = data.totalContributions || 0;
            stats.languagesContributed = new Set(data.languagesContributed || []);
            stats.placeTypesContributed = new Set(data.placeTypesContributed || []);
            stats.regionsContributed = new Set(data.regionsContributed || []);
            stats.contributionsByLanguage = new Map(data.contributionsByLanguage || []);
            stats.contributionsByPlaceType = new Map(data.contributionsByPlaceType || []);
            stats.contributionsByDate = new Map(data.contributionsByDate || []);
            stats.streakDays = data.streakDays || 0;
            stats.longestStreak = data.longestStreak || 0;
            stats.lastContributionDate = data.lastContributionDate;
            stats.joinDate = data.joinDate || Date.now();
            stats.qualityScore = data.qualityScore || 0;
            stats.badgesEarned = data.badgesEarned || [];
            stats.seenPlaces = data.seenPlaces || 0;
            stats.wikidataUploads = data.wikidataUploads || 0;
            stats.validationContributions = data.validationContributions || 0;
        }
        
        return stats;
    }
}

export default Statistics;